import { motion } from "framer-motion";
import { useLang } from "../../context/LanguageContext";
import { executionStrengths, images } from "../../data/content";
import OrbitPattern from "../OrbitPattern";

const ease = [0.22, 1, 0.36, 1];

const ExecutionStrengths = () => {
  const { isAr, t } = useLang();
  const supportCopy = t(
    "منظومة تنفيذ مصممة للمشاريع التي تتطلب اعتماداً واضحاً، إنتاجاً مستقراً، وتوريداً مرحلياً يحافظ على جودة المخرجات حتى التسليم.",
    "An execution model built for projects that require clear approvals, stable production, and phased supply without losing control of quality."
  );
  const highlights = [
    { value: "06", ar: "محاور تنفيذ", en: "execution pillars" },
    { value: "24h", ar: "استجابة أولية", en: "first response" },
    { value: "QA", ar: "رقابة مراحل", en: "stage control" },
  ];
  const previews = [
    images.doorManufacturing,
    images.productionLine,
    images.qualityCheck,
    images.factoryInterior,
    images.woodProduction,
    images.craftsman,
  ];

  return (
    <section
      className="relative bg-brand-green-deep text-bg-ivory py-[120px] md:py-[180px] overflow-hidden"
      data-testid="execution-section"
    >
      <div className="absolute -bottom-[200px] -start-[200px]">
        <OrbitPattern size={900} opacity={0.05} />
      </div>

      <div className="relative z-10 max-w-container mx-auto px-6 md:px-10 lg:px-14">
        {/* Header */}
        <div className="flex justify-between items-center text-bg-ivory/55 mb-16 border-b border-brand-champagne/20 pb-6" dir={isAr ? "rtl" : "ltr"}>
          <span className="font-mono text-sm tracking-widestest uppercase text-brand-champagne/80">[ {t("القدرات التنفيذية", "Execution Strength")} ]</span>
          <span className="font-mono text-sm">24 — SEC</span>
        </div>

        {/* Section Title & Description */}
        <div className="grid grid-cols-12 gap-y-8 gap-x-12 mb-20 md:mb-28" dir={isAr ? "rtl" : "ltr"}>
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, ease }}
            className="col-span-12 lg:col-span-6 flex flex-col justify-between"
          >
            <div>
              <div className={`${isAr ? "label-meta-ar" : "label-meta"} text-brand-champagne/80 mb-5`}>
                {t("[ قدراتنا التنفيذية ]", "[ EXECUTION STRENGTHS ]")}
              </div>
              <h3
                className={`${isAr ? "font-arabic font-medium" : "font-serif"} text-bg-ivory leading-[1.08] tracking-tight`}
                style={{ fontSize: isAr ? "clamp(38px, 4.8vw, 68px)" : "clamp(46px, 5.2vw, 84px)" }}
              >
                {isAr ? "قدراتنا التنفيذية" : (<>Execution <em className="font-light text-brand-champagne/95">Strengths</em></>)}
              </h3>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, delay: 0.15, ease }}
            className="col-span-12 lg:col-span-6 flex flex-col justify-between items-start lg:items-end"
          >
            <p className={`text-brand-champagne/70 leading-relaxed text-base max-w-[540px] mb-8 ${isAr ? "font-arabic text-right lg:ms-auto" : "font-sans text-left lg:me-auto"}`}>
              {supportCopy}
            </p>

            {/* Highlights bar */}
            <div className="flex gap-4 w-full lg:w-auto" dir={isAr ? "rtl" : "ltr"}>
              {highlights.map((item) => (
                <div key={item.value} className="flex-1 lg:flex-initial min-w-[100px] border border-brand-champagne/15 bg-brand-green-dark/30 backdrop-blur-sm px-4 py-3 text-center">
                  <div className="font-serif text-brand-champagne text-2xl lg:text-3xl leading-none font-light">
                    {item.value}
                  </div>
                  <div className={`font-mono text-[10px] tracking-widest uppercase mt-2 text-bg-ivory/50 ${isAr ? "font-arabic" : ""}`}>
                    {isAr ? item.ar : item.en}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* The Interactive Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" dir={isAr ? "rtl" : "ltr"}>
          {executionStrengths.map((s, i) => (
            <motion.div
              key={s.code}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.8, ease, delay: i * 0.1 }}
              className="group relative h-[280px] border border-brand-champagne/10 bg-brand-green-dark/20 backdrop-blur-md overflow-hidden cursor-pointer flex flex-col justify-between p-8 hover:border-brand-champagne/30 transition-all duration-500 rounded-sm"
            >
              {/* Card background image that fades & scales in on hover */}
              <div className="absolute inset-0 z-0 overflow-hidden">
                <div className="absolute inset-0 bg-brand-green-deep/90 z-10 transition-colors duration-500 group-hover:bg-brand-green-deep/55" />
                <img
                  src={previews[i]}
                  alt={isAr ? s.ar : s.en}
                  className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-[1.2s] ease-out opacity-0 group-hover:opacity-100"
                  style={{ filter: "saturate(0.6) contrast(1.1) brightness(0.85)", transition: "opacity 0.6s ease" }}
                />
              </div>

              {/* Huge low-contrast decorative number inside card */}
              <div 
                className={`absolute ${isAr ? "left-6" : "right-6"} top-4 font-serif text-[120px] leading-none text-brand-champagne/[0.03] group-hover:text-brand-champagne/[0.08] select-none pointer-events-none transition-colors duration-500`}
              >
                {s.code}
              </div>

              {/* Card Top: Code indicator */}
              <div className="relative z-20 flex justify-between items-center">
                <span className="font-mono text-xs text-brand-champagne/50 group-hover:text-brand-champagne transition-colors duration-300">
                  // {s.code}
                </span>
                <div className="w-1.5 h-1.5 rounded-full bg-brand-champagne/30 group-hover:bg-brand-champagne transition-all duration-300" />
              </div>

              {/* Card Bottom: Content */}
              <div className="relative z-20">
                <div className="w-12 h-px bg-brand-champagne/20 group-hover:w-20 transition-all duration-500 mb-6" />
                <h4 
                  className={`text-bg-ivory/80 group-hover:text-bg-ivory transition-colors duration-300 text-xl lg:text-2xl leading-snug ${isAr ? "font-arabic" : "font-serif italic font-light"}`}
                >
                  {isAr ? s.ar : s.en}
                </h4>
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default ExecutionStrengths;
